import {
  AnnouncementEntity,
  AnnouncementFlatTableEntity,
  Application,
  ApplicationDropdowns,
  FavoriteApplications,
  LabelInput
} from 'src/components/context/AppContextModels';

import {
  getApplicationDropdownsInSorted,
  parseAllAnnouncements,
  parseAllApplications,
  sortAndFilterAnnouncements
} from 'src/components/context/AppContextUtils';
import { getHomePageContentDefaultState } from 'src/components/das-finsuite/admin-components/homepage-manage/HomePageManagementUtils';
import { HomepageEntity } from 'src/components/das-finsuite/admin-components/homepage-manage/HomepageModel';
import { FinTechTeams } from 'src/components/das-finsuite/admin-components/teams-manage/TeamManagementModel';
import { getTeamContentDefaultState } from 'src/components/das-finsuite/admin-components/teams-manage/TeamManagementUtils';
import { appSyncOperationName } from 'src/constants/AppConstants';
import * as mutations from 'src/graphql/mutations';
import * as queries from 'src/graphql/queries';
import { fetchGraphQLRequest } from './graphql-api';
import { AllAppsWithParsed, DasFinSumFeedbackInput, AllReportsUnderLeaderResponse, ReportEmployeeData } from './interfaces';

const sortApplications = (apps: Application[]): Application[] =>
  apps.sort((a, b) => (a.applicationName ?? '').localeCompare(b.applicationName ?? ''));
export const fetchAllApplications = async (teamContent: FinTechTeams): Promise<AllAppsWithParsed> => {
  const { listApplications } = await fetchGraphQLRequest<{ listApplications: Application[] }>({
    query: queries.listApplications,
    variables: {},
    requestName: 'listApplications',
    requestType: appSyncOperationName.QUERY
  });

  const listApplicationsParsed = listApplications.map((app: Application) => {
    return { ...app, embeddingEnabled: app.embeddingEnabled ?? false, isFlagship: app.isFlagship ?? false };
  });
  return {
    allApplications: sortApplications(listApplicationsParsed),
    applicationFlatTableEntity: parseAllApplications(listApplications, teamContent)
  };
};

export const fetchApplicationDropdowns = async (teamContent: FinTechTeams): Promise<ApplicationDropdowns> => {
  const { listLabels } = await fetchGraphQLRequest<{ listLabels: LabelInput[] }>({
    query: queries.listLabels,
    variables: {},
    requestName: 'listLabels',
    requestType: appSyncOperationName.QUERY
  });
  return getApplicationDropdownsInSorted(listLabels, teamContent);
};

export const fetchFavoriteApplications = async (userAlias: string): Promise<string[]> => {
  const { listApplicationFavorites } = await fetchGraphQLRequest<{ listApplicationFavorites: FavoriteApplications[] }>({
    query: queries.listApplicationFavorites,
    variables: { userAlias },
    requestName: 'listApplicationFavorites',
    requestType: appSyncOperationName.QUERY
  });
  return listApplicationFavorites.find((favorite) => favorite.userAlias === userAlias)?.applicationId || [];
};

export const fetchAnnouncements = async (
  listOfApplications: Application[]
): Promise<{
  announcementList: AnnouncementEntity[];
  announcementsFlatTableEntity: AnnouncementFlatTableEntity[];
}> => {
  const { listAnnouncements } = await fetchGraphQLRequest<{ listAnnouncements: AnnouncementEntity[] }>({
    query: queries.listAnnouncements,
    variables: {},
    requestName: 'listAnnouncements',
    requestType: appSyncOperationName.QUERY
  });
  return {
    announcementList: sortAndFilterAnnouncements(listOfApplications, listAnnouncements),
    announcementsFlatTableEntity: parseAllAnnouncements(listOfApplications, listAnnouncements)
  };
};
export const fetchHomePageContent = async (alias: string): Promise<HomepageEntity> => {
  const data = await fetchGraphQLRequest<{ listHomePageContent: HomepageEntity }>({
    query: queries.listHomePageContent,
    variables: {},
    requestName: 'listHomePageContent',
    requestType: appSyncOperationName.QUERY
  });
  return data.listHomePageContent?.id ? data.listHomePageContent : getHomePageContentDefaultState(alias);
};

export const fetchTeamPageContent = async (alias: string): Promise<FinTechTeams> => {
  const data = await fetchGraphQLRequest<{ listTeamPageContent: any }>({
    query: queries.listTeamPageContent,
    variables: {},
    requestName: 'listTeamPageContent',
    requestType: appSyncOperationName.QUERY
  });

  return data.listTeamPageContent?.id ? data.listTeamPageContent : getTeamContentDefaultState(alias);
};

export const updateHomePageContentMutation = async (homepageEntity: HomepageEntity): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.updateHomePageContent,
    variables: { input: homepageEntity },
    requestName: 'updateHomePageContent',
    requestType: appSyncOperationName.MUTATION
  });
};

export const updateTeamPageContentMutation = async (teamPageContent: FinTechTeams): Promise<any> => {
  return fetchGraphQLRequest<any>({
    query: mutations.updateTeamPageContent,
    variables: { input: teamPageContent },
    requestName: 'updateTeamPageContent',
    requestType: appSyncOperationName.MUTATION
  });
};

export const updateDasFinSumFeedbackMutation = async (input: DasFinSumFeedbackInput): Promise<{ updateDasFinSumFeedback: boolean }> => {
  return fetchGraphQLRequest<{ updateDasFinSumFeedback: boolean }>({
    query: mutations.updateDasFinSumFeedback,
    variables: { input },
    requestName: 'updateDasFinSumFeedback',
    requestType: 'mutation'
  });
};

interface ListAllReportsVariables {
  supervisor_login: string;
  hierarchy_distance?: number;
}

export const fetchAllReportsUnderLeader = async (supervisorLogin: string, hierarchy_distance?: number): Promise<ReportEmployeeData[]> => {
  const variables: ListAllReportsVariables = { supervisor_login: supervisorLogin };

  // Only add hierarchy_distance if explicitly provided
  if (hierarchy_distance !== undefined) {
    variables.hierarchy_distance = hierarchy_distance;
  }

  const data = await fetchGraphQLRequest<AllReportsUnderLeaderResponse>({
    query: queries.listAllReportsUnderLeader,
    variables,
    requestName: 'listAllReportsUnderLeader',
    requestType: appSyncOperationName.QUERY,
    authMode: 'iam'
  });

  return data.listAllReportsUnderLeader?.reports_under_leader?.data || [];
};

// ── Finance Calendar ────────────────────────────────────────

import { FinanceCalendarEventEntity } from 'src/components/das-finsuite/widgets/finance-calendar-widget/FinanceCalendarData';
import { FinanceCalendarEventInput } from 'src/API';

export const fetchFinanceCalendarEvents = async (): Promise<FinanceCalendarEventEntity[]> => {
  const data = await fetchGraphQLRequest<{ listFinanceCalendarEvents: FinanceCalendarEventEntity[] }>({
    query: queries.listFinanceCalendarEvents,
    variables: {},
    requestName: 'listFinanceCalendarEvents',
    requestType: appSyncOperationName.QUERY
  });
  return data.listFinanceCalendarEvents || [];
};

export const putFinanceCalendarEventsMutation = async (events: FinanceCalendarEventInput[]): Promise<FinanceCalendarEventEntity[]> => {
  const data = await fetchGraphQLRequest<{ putFinanceCalendarEvents: FinanceCalendarEventEntity[] }>({
    query: mutations.putFinanceCalendarEvents,
    variables: { events },
    requestName: 'putFinanceCalendarEvents',
    requestType: appSyncOperationName.MUTATION
  });
  return data.putFinanceCalendarEvents || [];
};

export const deleteFinanceCalendarEventMutation = async (eventId: string): Promise<string> => {
  const data = await fetchGraphQLRequest<{ deleteFinanceCalendarEvent: string }>({
    query: mutations.deleteFinanceCalendarEvent,
    variables: { eventId },
    requestName: 'deleteFinanceCalendarEvent',
    requestType: appSyncOperationName.MUTATION
  });
  return data.deleteFinanceCalendarEvent;
};
